/**
 * usePWA — the gym page's half of the PWA wiring: it swaps the manifest link
 * (and the Apple meta tags) to the gym's own manifest, so an install started
 * from here produces the GYM app rather than the platform one.
 *
 * The install banner's own state lives in `useInstallBanner`, shared with the
 * gym selector — see that hook's header for which app a tap installs, and why
 * the dismiss flag is deliberately not per gym.
 */
import { useEffect } from "react";
import { isFeatureEnabled } from "../utils";
import { DEFAULT_ACCENT } from "../constants";
import { useInstallBanner } from "./useInstallBanner";

export function usePWA({ gymMeta, isStandalone }) {
  const pwaEnabled = isFeatureEnabled(gymMeta, "gymPwa");
  const banner = useInstallBanner({
    isStandalone,
    // The gym's manifest has to be the live one before an install can mean the
    // gym — and without a slug there is no gym manifest to point at.
    eligible: pwaEnabled && !!gymMeta?.slug,
    gymId: gymMeta?.id,
  });

  // Set manifest link (removed from index.html to avoid race condition)
  useEffect(() => {
    const manifestUrl = pwaEnabled && gymMeta?.slug
      ? `/manifest/${gymMeta.slug}.json`
      : "/manifest.json";

    // Remove any existing manifest link and insert fresh one to force Chrome re-evaluation
    const old = document.querySelector('link[rel="manifest"]');
    if (old) old.remove();
    const link = document.createElement("link");
    link.rel = "manifest";
    link.href = manifestUrl;
    document.head.appendChild(link);

    if (!pwaEnabled || !gymMeta?.slug) return;

    // Update theme-color meta
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta) themeMeta.content = gymMeta.accentColor || DEFAULT_ACCENT;

    // Update Apple PWA meta tags
    const appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (appleTitleMeta) appleTitleMeta.content = gymMeta.name || "Sendly";

    // Update Apple touch icon to dynamic icon endpoint
    const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (appleTouchIcon) appleTouchIcon.href = `/manifest/${gymMeta.slug}/icon.png`;
  }, [pwaEnabled, gymMeta?.slug, gymMeta?.name, gymMeta?.accentColor, gymMeta?.logoUrl, gymMeta?.description]);

  return banner;
}
